import React from 'react';
import {
  AuthProvider,
  Honeybadger,
  ServiceWorkerEvents,
  Toaster,
  useAuth,
  useAutoLogout,
  useNotifySWs,
  useVideoCallRedirects,
  useVideoChat,
  VideoChat,
  DictionariesContextProvider,
  useWindowSizeCssVar,
  AppChatContextProvider,
  useServiceWorker,
  honeybadgerConfig,
} from '@monument/core';
import { Elements as StripeElements } from '@stripe/react-stripe-js';
import { Honeybadger as HoneybadgerBase, HoneybadgerErrorBoundary } from '@honeybadger-io/react';
import { OnboardingContextProvider } from '@services/onboarding/useOnboarding';
import { MembershipContextProvider } from '@services/membership/context';
import { NotificationsProvider } from '@services/notifications';
import { VideoChatWrapper } from '@components/VideoChatWrapper';
import { InsuranceContextProvider } from '@services/insurance/useInsurance';
import { PostAppointmentStateProvider } from '@services/post-appointment/PostAppointmentContext';
import { BackBarStoreContextProvider } from '@shared/contexts/useBackBarStore';
import { loadStripe } from '@stripe/stripe-js';
import { BrowserRouter } from 'react-router-dom';
import { AppointmentSchedulingContextProvider } from '@services/scheduling/useAppointmentScheduling';

const AUTH_IGNORE_PATHS = ['/signup', '/signup/:step', '/cookie-list'];

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY as string);

const stripeElementsOptions = {
  fonts: [
    {
      src: `url(${process.env.REACT_APP_APPLICATION_URL}/public-assets/CentraNo1-Book.woff)`,
      family: 'CentraNo1',
    },
  ],
};

// Phase 3: initialization that depends on parent providers.
// @TODO: consider refactoring these initializers into the providers directly.
const InitializeAppPhase3 = ({ children }) => {
  const { isAuthenticated, logout } = useAuth();
  const { isVideoActive } = useVideoChat();

  // Create --window-(width|height) CSS vars (more accurate 100vh for iOS Safari)
  useWindowSizeCssVar();

  useVideoCallRedirects();

  useAutoLogout({ enabled: !isVideoActive, logout, trackEvents: true });

  useNotifySWs({ allowed: isAuthenticated, message: ServiceWorkerEvents.LOGIN });

  useServiceWorker();

  return (
    <>
      {children}
      {/* Render VideoChat here so that the widget can exist on any route
      and patient can to go to different parts of the app while on a call */}
      {isAuthenticated && isVideoActive && <VideoChat allowFullScreen />}
      {/* Increased distance from top/bottom to avoid content like the profile icon */}
      <Toaster position="top-right" containerStyle={{ top: '76px', bottom: '100px' }} />
    </>
  );
};

// Phase 2: global app data
// @TODO: review scope of each provider, potentially combine some / simplify
const InitializeAppPhase2 = ({ children }) => (
  <DictionariesContextProvider>
    <BackBarStoreContextProvider>
      <MembershipContextProvider>
        <InsuranceContextProvider>
          <OnboardingContextProvider>
            <PostAppointmentStateProvider>
              <AppointmentSchedulingContextProvider>
                <VideoChatWrapper>
                  <NotificationsProvider>{children}</NotificationsProvider>
                </VideoChatWrapper>
              </AppointmentSchedulingContextProvider>
            </PostAppointmentStateProvider>
          </OnboardingContextProvider>
        </InsuranceContextProvider>
      </MembershipContextProvider>
    </BackBarStoreContextProvider>
  </DictionariesContextProvider>
);

// Phase 1: app-wide configuration + 3rd-party dependencies + auth
const InitializeAppPhase1 = ({ children }) => (
  <HoneybadgerErrorBoundary honeybadger={HoneybadgerBase.configure(honeybadgerConfig)}>
    <StripeElements stripe={stripePromise} options={stripeElementsOptions}>
      <AuthProvider ignorePaths={AUTH_IGNORE_PATHS}>
        <BrowserRouter basename={`${process.env.PUBLIC_URL}`}>
          <Honeybadger />
          <AppChatContextProvider>{children}</AppChatContextProvider>
        </BrowserRouter>
      </AuthProvider>
    </StripeElements>
  </HoneybadgerErrorBoundary>
);

// @TODO: is there some helper for more nicely composing these phases together without raw JSX?
const InitializeApp = ({ children }) => (
  <InitializeAppPhase1>
    <InitializeAppPhase2>
      <InitializeAppPhase3>{children}</InitializeAppPhase3>
    </InitializeAppPhase2>
  </InitializeAppPhase1>
);

export default InitializeApp;
